import React, { useEffect, useState } from 'react';
import { Button, Col, Dropdown, DropdownButton, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BsSliders, BsSortDown, BsSortUp } from 'react-icons/bs';
import { FaTh, FaThList } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import { IParams } from '../../../../../model/shared/IParams';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { getStatusIcon, isDateInPast, truncateString } from '../../../../../shared/functions/Functions';
import { IAllStates } from '../../../../../store/rootReducer';
import { IOfferFilterProps } from './dto/IOfferFilterProps';
import { useSocialPlatform } from '../../../../../shared/hooks/useSocialPlatform';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { SearchInput } from '../../../../../shared/components/SearchInput';

const offerStatusColors: Record<string, string> = {
    received: 'warning',
    sent: 'info',
    booked: 'success',
    declined: 'danger',
    proposed: 'primary',
};

const blockSignKeys = (event: React.KeyboardEvent) =>
    (event?.key === '-' || event?.key === '+') && event.preventDefault();

export function BookingFilter({
    setActiveCard,
    activeCard,
    info,
    offerCount,
    setFilterParams,
    filterParams,
    clearSelectedOffer,
    isFilterLoading = false,
}: IOfferFilterProps) {
    const navigate = useNavigate();
    const { country, OfferFilteringEnum } = useSelector((states: IAllStates) => states.enums);
    const { isClient } = useSelector((states: IAllStates) => states.auth.user);
    const { platforms: socialPlatforms } = useSocialPlatform({ isEnabled: true });
    const [selectedFilters, setSelectedFilters] = useState<any>();
    const [isFilterShown, setIsFilterShown] = useState(false);
    const [prevFilter, setPrevFilter] = useState('');
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>();

    const statusHandler = (event: any) => {
        const value = event.currentTarget.name;

        if (isClient) {
            setFilterParams(prev => ({
                ...prev,
                offerFilters: value,
                page: 1,
                perPage: 10,
            }));
            setPrevFilter(value);
            return;
        }

        setFilterParams(prev => ({
            ...prev,
            offerFilters: value === prevFilter ? undefined : value,
            page: 1,
            perPage: 10,
        }));

        setPrevFilter(value === prevFilter ? '' : value);
    };

    const multipleSelectHandler = (paramKey: string, paramValue: any) => {
        setFilterParams(prev => ({
            ...prev,
            [paramKey]: !!`${paramValue}`?.length ? paramValue : null,
            page: 1,
            perPage: 10,
        }));
    };

    const handleSearch = (value: string, name: string) => {
        setFilterParams(prev => ({ ...prev, [name]: value, page: 1, perPage: 10 }));
    };

    useEffect(() => {
        if (filterParams?.platformId) {
            setSelectedFilters((prev: any) => ({
                ...prev,
                selectedSocialPlatform: socialPlatforms?.find(
                    platform => platform?.id === Number(filterParams?.platformId)
                ),
            }));
        }
    }, [JSON.stringify(filterParams?.platformId)]);

    const audienceCountries = filterParams?.audienceLocation?.map(item => t(`general.countries.${item}`));
    const influencerCountries = filterParams?.influencerLocation?.map(item => t(`general.countries.${item}`));
    const filteredCountries = (audienceCountries ?? [])?.concat(influencerCountries ?? []);

    const openInvitePage = () => {
        navigate(`${InternalRoutes.BRAND_CAMPAIGN_BOOK}/${id}/invite`);
    };

    const getNotificationCount = (key: string) => {
        switch (key) {
            case 'received':
                return 'receivedCount';
            case 'sent':
                return 'sentCount';
            case 'declined':
                return 'declinedCount';
            case 'proposed':
                return 'proposedCount';
            default:
                return 'bookingCount';
        }
    };

    const sortedOfferStatuses = React.useMemo(() => {
        if (!OfferFilteringEnum) return OfferFilteringEnum;
        if (isClient) {
            return ['proposed', 'booked'].filter(status => OfferFilteringEnum.includes(status));
        }
        const rest = OfferFilteringEnum.filter(status => status !== 'proposed');
        return (info?.proposedCount ?? 0) > 0 && OfferFilteringEnum.includes('proposed') ? ['proposed', ...rest] : rest;
    }, [OfferFilteringEnum, info?.proposedCount, isClient]);

    const sortControls = (
        <div className="d-flex align-items-center justify-content-end">
            <Button
                onClick={() =>
                    setFilterParams({
                        ...filterParams,
                        sortType: filterParams.sortType === 'DESC' ? 'ASC' : 'DESC',
                    })
                }
                variant="transparent"
                aria-label="Sort type"
            >
                {filterParams.sortType === 'ASC' ? (
                    <BsSortUp style={{ height: 16, width: 16 }} />
                ) : (
                    <BsSortDown style={{ height: 16, width: 16 }} />
                )}
            </Button>
            <DropdownButton
                className="mr-2 font-weight-normal"
                variant="transparent"
                title={
                    <span>
                        {t(
                            !filterParams?.sortCriteria
                                ? 'general.button.sort_by'
                                : `brand.campaign.book.offer.filters.${filterParams?.sortCriteria}`
                        )}
                    </span>
                }
            >
                {filterParams?.sortCriteria && (
                    <Dropdown.Item
                        onClick={() =>
                            setFilterParams({
                                ...filterParams,
                                sortCriteria: '',
                            })
                        }
                    >
                        Clear
                    </Dropdown.Item>
                )}
                <Dropdown.Item
                    onClick={() =>
                        setFilterParams({
                            ...filterParams,
                            sortCriteria: 'totalFollowers',
                        })
                    }
                >
                    {t('brand.campaign.book.offer.filters.totalFollowers')}
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() =>
                        setFilterParams({
                            ...filterParams,
                            sortCriteria: 'mostActive',
                        })
                    }
                >
                    {t('brand.campaign.book.offer.filters.mostActive')}
                </Dropdown.Item>

                <Dropdown.Item
                    onClick={() =>
                        setFilterParams({
                            ...filterParams,
                            sortCriteria: 'submissionDate',
                        })
                    }
                >
                    {t('brand.campaign.book.offer.filters.submissionDate')}
                </Dropdown.Item>

                <Dropdown.Item
                    onClick={() =>
                        setFilterParams({
                            ...filterParams,
                            sortCriteria: 'contentUnits',
                        })
                    }
                >
                    {t('brand.campaign.book.offer.filters.contentUnits')}
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() =>
                        setFilterParams({
                            ...filterParams,
                            sortCriteria: 'price',
                        })
                    }
                >
                    {t('brand.campaign.book.offer.filters.price')}
                </Dropdown.Item>
            </DropdownButton>
            <Button
                onClick={() => setActiveCard(true)}
                variant="icon"
                className={`mr-2 ${activeCard ? 'active' : ''}`}
                aria-label="List view"
            >
                <FaTh />
            </Button>
            <Button
                onClick={() => setActiveCard(false)}
                variant="icon"
                className={` ${activeCard ? '' : 'active'}`}
                aria-label="List view"
            >
                <FaThList />
            </Button>
        </div>
    );

    return (
        <>
            <div className="d-flex flex-wrap justify-content-between align-items-center pb-md-3 pb-1 gap-3">
                <div className="d-flex flex-wrap gap-3 col">
                    <div className="px-0">
                        <Button
                            variant="outline-secondary"
                            aria-label="Sort"
                            onClick={() => setIsFilterShown(prev => !prev)}
                        >
                            {!isFilterShown ? (
                                <BsSliders size={15} className="icon-dark" />
                            ) : (
                                <IoMdClose className="icon-dark" size={15} />
                            )}
                        </Button>
                    </div>
                    {sortedOfferStatuses?.map((offerStatus, index) => {
                        return (
                            <div key={offerStatus + index} className="px-0 col col-md-auto">
                                <Button
                                    onClick={statusHandler}
                                    name={offerStatus}
                                    disabled={isFilterLoading}
                                    className={`d-flex align-items-center justify-content-center min-width-max-content w-100 w-md-auto text-capitalize ${
                                        filterParams?.offerFilters === offerStatus
                                            ? `btn-outline-${offerStatusColors[offerStatus]} active`
                                            : `btn-outline-muted ${offerStatus}`
                                    }`}
                                >
                                    <span
                                        className={`d-flex align-items-center ${
                                            filterParams?.offerFilters === offerStatus && !isFilterLoading
                                                ? 'text-light'
                                                : 'text-dark'
                                        }`}
                                    >
                                        {getStatusIcon(offerStatus, 'mr-2 d-flex')}
                                        {t(`brand.campaign.book.offer.filters.${offerStatus}`)} (
                                        {info?.[getNotificationCount(offerStatus)] ?? 0})
                                    </span>
                                </Button>
                            </div>
                        );
                    })}
                    {/*<div className="d-md-none w-100">*/}
                    {/*    <Button*/}
                    {/*        name="clearSelection"*/}
                    {/*        className="btn btn-clear-selection text-uppercase w-100"*/}
                    {/*        onClick={clearSelectedOffer}*/}
                    {/*    >*/}
                    {/*        {t('brand.campaign.book.offer.filters.clear_selection')}*/}
                    {/*    </Button>*/}
                    {/*</div>*/}
                </div>
                <div className="mb-2 mb-md-0 d-flex align-items-center col-12 col-md-auto px-0">
                    {!isClient && info?.campaign && (
                        <TooltipOverlay
                            content={
                                info.campaign.approvalStatus === 'pending' ||
                                isDateInPast(info.campaign.publishTo ?? '') ? (
                                    <>
                                        {info.campaign.approvalStatus === 'pending'
                                            ? t('brand.campaign.book.invite.filters.not_approved_campaign')
                                            : t('brand.campaign.book.offer.filters.influencer_search_tooltip')}
                                    </>
                                ) : null
                            }
                        >
                            <span
                                className={`w-100
                                    ${
                                        info.campaign.approvalStatus === 'pending' ||
                                        isDateInPast(info.campaign.publishTo)
                                            ? 'cursor-not-allowed'
                                            : ''
                                    }
                                `}
                            >
                                <Button
                                    variant="outline-primary"
                                    className="w-100 text-uppercase d-flex align-items-center justify-content-center"
                                    disabled={
                                        info.campaign.approvalStatus === 'pending' ||
                                        isDateInPast(info.campaign.publishTo ?? '')
                                    }
                                    onClick={openInvitePage}
                                >
                                    <i className="fi fi-rr-user-add mr-1" />
                                    <span>{t('brand.campaign.book.invite.filters.request_additional_offers')}</span>
                                </Button>
                            </span>
                        </TooltipOverlay>
                    )}
                </div>
            </div>

            {isFilterShown && (
                <Row className="pb-md-3 pb-1">
                    <Col md={2} xs={6} className="my-2 my-md-0">
                        <SearchInput
                            name="displayName"
                            defaultValue={filterParams?.displayName ?? ''}
                            onSearch={handleSearch}
                            isLoading={isFilterLoading}
                            label={t('general.display_name')}
                        />
                    </Col>
                    <Col md={3} xs={6} className="my-2 my-md-0">
                        <DropdownButton
                            className={`dropdown-secondary location-dropdown ${
                                filterParams?.reachFrom ? 'active' : ''
                            }`}
                            title={`${t('brand.campaign.book.offer.filters.reach')}
                        ${filterParams?.reachFrom} ${filterParams?.reachFrom ? '-' : ''} ${filterParams?.reachTo}`}
                        >
                            <Row>
                                <Col md={6}>
                                    <SearchInput
                                        name="reachFrom"
                                        type="number"
                                        defaultValue={filterParams?.reachFrom ?? ''}
                                        onSearch={handleSearch}
                                        isLoading={isFilterLoading}
                                        label={t('brand.campaign.book.offer.filters.reach_from')}
                                        className="w-100 mb-4 my-md-0"
                                        InputProps={{ inputProps: { min: 0 } }}
                                        onKeyPress={blockSignKeys}
                                    />
                                </Col>
                                <Col md={6}>
                                    <SearchInput
                                        name="reachTo"
                                        type="number"
                                        defaultValue={filterParams?.reachTo ?? ''}
                                        onSearch={handleSearch}
                                        isLoading={isFilterLoading}
                                        label={t('brand.campaign.book.offer.filters.reach_to')}
                                        InputProps={{ inputProps: { min: 0 } }}
                                        onKeyPress={blockSignKeys}
                                    />
                                </Col>
                            </Row>
                        </DropdownButton>
                    </Col>
                    <Col md={2} xs={6} className="my-2 my-md-0">
                        <CustomAutocomplete
                            dataArray={socialPlatforms}
                            isWithImage={true}
                            inputLabel="name"
                            photoLabel="path"
                            isMultiple={true}
                            selectedStateImage={selectedFilters?.selectedSocialPlatform?.path as string}
                            queryFilterKey="platformId"
                            inputLabelTranslation={`${t('brand.campaign.book.offer.filters.platform')}`}
                            handleChangeParams={multipleSelectHandler}
                            isDisabled={isFilterLoading}
                        />
                    </Col>
                    <Col md={2} xs={6} className="my-2 my-md-0">
                        <DropdownButton
                            className={`dropdown-secondary location-dropdown ${
                                !!filteredCountries?.length ? 'active' : ''
                            }`}
                            title={`${
                                !filteredCountries?.length ? t('brand.campaign.book.offer.filters.location') : ''
                            } ${truncateString(
                                filteredCountries
                                    ?.map(item => item)
                                    .slice(0, 3)
                                    ?.join(', '),
                                18
                            )} ${
                                (filteredCountries?.length ?? 0) > 3 ? `+ ${(filteredCountries?.length ?? 0) - 3}` : ''
                            }`}
                        >
                            <Row>
                                <Col xl={6}>
                                    <h6 className="mb-3">{t('brand.campaign.book.offer.filters.audience_location')}</h6>
                                    <CustomAutocomplete
                                        dataArray={country}
                                        isWithImage={true}
                                        inputLabel="country"
                                        isMultiple={true}
                                        photoLabel="countryFlag"
                                        queryFilterKey="audienceLocation"
                                        inputLabelTranslation={`${t('brand.campaign.book.offer.filters.location')}`}
                                        handleChangeParams={multipleSelectHandler}
                                        isDisabled={isFilterLoading}
                                    />
                                </Col>
                                <Col xl={6}>
                                    <h6 className="mt-3 mt-lg-0 mb-3">
                                        {t('brand.campaign.book.offer.filters.influencer_location')}
                                    </h6>
                                    <CustomAutocomplete
                                        dataArray={country}
                                        isWithImage={true}
                                        inputLabel="country"
                                        isMultiple={true}
                                        photoLabel="countryFlag"
                                        queryFilterKey="influencerLocation"
                                        inputLabelTranslation={`${t('brand.campaign.book.offer.filters.add_location')}`}
                                        handleChangeParams={multipleSelectHandler}
                                        isDisabled={isFilterLoading}
                                    />
                                </Col>
                            </Row>
                        </DropdownButton>
                    </Col>
                    <Col md={3} xs={6} className="my-2 my-md-0">
                        <DropdownButton
                            className={`dropdown-secondary location-dropdown ${
                                filterParams?.priceRangeFrom || filterParams?.priceRangeTo ? 'active' : ''
                            }`}
                            title={`${t('brand.campaign.book.offer.filters.price_range')}
                        ${filterParams?.priceRangeFrom} ${filterParams?.priceRangeFrom ? '-' : ''} ${
                                filterParams?.priceRangeTo
                            }`}
                        >
                            <Row>
                                <Col lg={6}>
                                    <SearchInput
                                        name="priceRangeFrom"
                                        type="number"
                                        defaultValue={filterParams?.priceRangeFrom ?? ''}
                                        onSearch={handleSearch}
                                        isLoading={isFilterLoading}
                                        label={t('brand.campaign.book.offer.filters.price_from')}
                                        className="w-100 mb-4 my-lg-0"
                                        InputProps={{ inputProps: { min: 0 } }}
                                        onKeyPress={blockSignKeys}
                                    />
                                </Col>
                                <Col lg={6}>
                                    <SearchInput
                                        name="priceRangeTo"
                                        type="number"
                                        defaultValue={filterParams?.priceRangeTo ?? ''}
                                        onSearch={handleSearch}
                                        isLoading={isFilterLoading}
                                        label={t('brand.campaign.book.offer.filters.price_to')}
                                        InputProps={{ inputProps: { min: filterParams?.priceRangeFrom } }}
                                        onKeyPress={blockSignKeys}
                                    />
                                </Col>
                            </Row>
                        </DropdownButton>
                    </Col>
                </Row>
            )}
        </>
    );
}
